import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import PageTitle from './shared/PageTitle';
import { useLazyQuery, useQuery } from '@apollo/client';
import { getLovs } from '../queries';
import PaymentVouchersTable from './payment-vouchers/PaymentVouchersTable';
import { IFormValues } from './ViewBreakdown';
import { enumListAsRecordObject, getError } from '@utils/graph-utils';
import ViewBreakdownShell from './ViewBreakdownShell';
import FacultativesWidget from './facultatives/FacultativesWidget';
import { useParams } from 'react-router-dom';
import { getFacOfPoliciesQuery } from './facultatives/queries';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import UnallocatedPayments from './shared/UnallocatedPayments';

interface IMainColumnProps {
  mainEntityId: string;
  businessPartnerType: string;
}

const useStyles = makeStyles()(() => ({
  container: {
    marginTop: '20px',
  },
}));

const MainColumn: React.FC<IMainColumnProps> = ({
  mainEntityId,
  businessPartnerType,
}) => {
  const { id: currentReinsurerId } = useParams<{ id: string }>();
  const { classes } = useStyles();
  const [fetchLovs, { data: lovs }] = useLazyQuery(getLovs);
  const {
    refetch: refetchFacOfPolicies,
    data: facsData,
    error,
    loading: facsLoading,
  } = useQuery(getFacOfPoliciesQuery, {
    variables: {
      ReinsurerID: currentReinsurerId,
    },
    errorPolicy: 'all',
    skip: businessPartnerType !== 'Reinsurer',
  });
  const [viewBreakdownPopup, setViewBreakdownPopup] = useState<{
    open: boolean;
    selected?: {
      id: string;
    } & IFormValues;
  }>({
    open: false,
  });

  if (error) {
    toast.error(
      <ToastErrorMessage>{getError({ errors: error })}</ToastErrorMessage>
    );
  }

  useEffect(() => {
    fetchLovs();
  }, [fetchLovs, mainEntityId]);

  const paymentMethods = useMemo(() => {
    if (!lovs) return {};

    return enumListAsRecordObject(
      lovs.Accounting_PaymentMethodList?.enumValues as {
        __typename: string;
        name: string;
      }[]
    );
  }, [lovs]);

  return (
    <div>
      {viewBreakdownPopup.open && (
        <ViewBreakdownShell
          open={viewBreakdownPopup.open}
          policyId={viewBreakdownPopup.selected?.id}
          businessPartnerId={mainEntityId}
          data={viewBreakdownPopup.selected}
          onClose={() => {
            setViewBreakdownPopup({
              open: false,
              selected: undefined,
            });
          }}
        />
      )}
      <PageTitle text={'Payables'} />
      {businessPartnerType === 'Reinsurer' && (
        <div className={classes.container}>
          <FacultativesWidget data={facsData} loading={facsLoading} />
        </div>
      )}
      <div className={classes.container}>
        <PaymentVouchersTable
          paymentMethods={paymentMethods}
          onSuccess={() => refetchFacOfPolicies()}
        />
      </div>
      <div className={classes.container}>
        <UnallocatedPayments type="DEBIT" />
      </div>
    </div>
  );
};

export default MainColumn;
